import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ManagedPlatformUser, PlatformRole, PlanStatus, SubscriptionPlan } from '../models/platform-access.models';

export interface AdminApiError {
  code: number;
  message: string;
  details?: string[];
}

export interface AdminUserListState {
  users: ManagedPlatformUser[];
  roles: PlatformRole[];
  plans: SubscriptionPlan[];
}

export interface AdminPlansState {
  plans: SubscriptionPlan[];
}

export interface AdminAssistantCreditPack {
  publicId: string;
  label: string;
  description: string | null;
  tokenAmount: number;
  priceCents: number;
  currency: 'EUR';
  isActive: boolean;
  sortOrder: number;
  paddleProductId: string | null;
  paddlePriceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateManagedUserRequest {
  email: string;
  password: string;
  name?: string | null;
  role: PlatformRole;
  planCode: string;
  isActive?: boolean;
}

export interface UpdateManagedUserRequest {
  name?: string | null;
  nickname?: string | null;
  role?: PlatformRole;
  planCode?: string;
  planStatus?: PlanStatus;
  isActive?: boolean;
}

interface AdminListUsersResponse {
  success: boolean;
  users: ManagedPlatformUser[];
  roles: PlatformRole[];
  plans: SubscriptionPlan[];
}

interface AdminListPlansResponse {
  success: boolean;
  plans: SubscriptionPlan[];
}

interface AdminCreditPacksResponse {
  success: boolean;
  packs: AdminAssistantCreditPack[];
}

interface AdminCreditPackMutationResponse {
  success: boolean;
  pack: AdminAssistantCreditPack;
}

interface AdminUserMutationResponse {
  success: boolean;
  user: ManagedPlatformUser;
}

interface AdminPlanMutationResponse {
  success: boolean;
  message: string;
  plan: SubscriptionPlan;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private readonly API_URL = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getUsers(): Observable<AdminUserListState> {
    return this.http
      .get<AdminListUsersResponse>(`${this.API_URL}/admin/users`)
      .pipe(
        map((response) => ({
          users: response.users,
          roles: response.roles,
          plans: response.plans
        })),
        catchError(this.handleError)
      );
  }

  createUser(payload: CreateManagedUserRequest): Observable<ManagedPlatformUser> {
    return this.http
      .post<AdminUserMutationResponse>(`${this.API_URL}/admin/users`, payload)
      .pipe(map((response) => response.user), catchError(this.handleError));
  }

  updateUser(userPublicId: string, payload: UpdateManagedUserRequest): Observable<ManagedPlatformUser> {
    return this.http
      .patch<AdminUserMutationResponse>(`${this.API_URL}/admin/users/${userPublicId}`, payload)
      .pipe(map((response) => response.user), catchError(this.handleError));
  }

  getPlans(): Observable<AdminPlansState> {
    return this.http
      .get<AdminListPlansResponse>(`${this.API_URL}/admin/plans`)
      .pipe(
        map((response) => ({
          plans: response.plans
        })),
        catchError(this.handleError)
      );
  }

  updatePlan(
    planCode: string,
    payload: {
      name?: string;
      description?: string | null;
      priceEur?: number;
      maxActiveWebsites?: number | null;
      maxTeamMembers?: number | null;
      allowCustomDomain?: boolean;
      allowExport?: boolean;
      allowPublish?: boolean;
      templateAccessLevel?: string;
      supportLevel?: string;
      selfServeVisible?: boolean;
      paddleBillingEnabled?: boolean;
      assistantIncludedTokens?: number;
      sortOrder?: number;
    }
  ): Observable<{ message: string; plan: SubscriptionPlan }> {
    return this.http
      .patch<AdminPlanMutationResponse>(`${this.API_URL}/admin/plans/${planCode}`, payload)
      .pipe(
        map((response) => ({
          message: response.message,
          plan: response.plan
        })),
        catchError(this.handleError)
      );
  }

  getAssistantCreditPacks(): Observable<AdminAssistantCreditPack[]> {
    return this.http
      .get<AdminCreditPacksResponse>(`${this.API_URL}/admin/assistant-credit-packs`)
      .pipe(map((response) => response.packs), catchError(this.handleError));
  }

  createAssistantCreditPack(payload: {
    label: string;
    description?: string | null;
    tokenAmount: number;
    priceCents: number;
    currency: 'EUR';
    isActive: boolean;
    sortOrder: number;
  }): Observable<AdminAssistantCreditPack> {
    return this.http
      .post<AdminCreditPackMutationResponse>(`${this.API_URL}/admin/assistant-credit-packs`, payload)
      .pipe(map((response) => response.pack), catchError(this.handleError));
  }

  updateAssistantCreditPack(
    creditPackId: string,
    payload: Partial<{
      label: string;
      description: string | null;
      tokenAmount: number;
      priceCents: number;
      currency: 'EUR';
      isActive: boolean;
      sortOrder: number;
    }>
  ): Observable<AdminAssistantCreditPack> {
    return this.http
      .patch<AdminCreditPackMutationResponse>(`${this.API_URL}/admin/assistant-credit-packs/${encodeURIComponent(creditPackId)}`, payload)
      .pipe(map((response) => response.pack), catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let message = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      message = error.error.error.message as string;
    } else if (error.error?.message) {
      message = error.error.message as string;
    } else if (error.message) {
      message = error.message;
    }

    return throwError(() => ({
      code: error.status,
      message,
      details: error.error?.error?.details as string[] | undefined,
    } as AdminApiError));
  };
}
